import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { sanitizeSupabaseError } from '@/lib/errorHandler';
import { captureError } from '@/lib/sentry';
import { useOrganization } from '@/hooks/useOrganization';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { TFunction } from 'i18next';
import {
  listClientes,
  getClienteById,
  atualizarCliente,
  editarCliente,
  atualizarStatusCliente,
  criarCliente,
  assumirAtendimento,
  excluirCliente,
  arquivarCliente,
  desarquivarCliente,
  transferirAtendimento,
  ClienteJaAtribuidoError,
  type StatusCliente,
  type ClienteInsert,
  type ClienteUpdate,
} from '@/services/clientes.service';

export const getStatusLabels = (t: TFunction): Record<StatusCliente, string> => ({
  novo: t('clients.status.new'),
  em_qualificacao: t('clients.status.qualifying'),
  aguardando_atendente: t('clients.status.awaitingAgent'),
  em_atendimento: t('clients.status.inService'),
  finalizado: t('clients.status.finished'),
  perdido: t('clients.status.lost'),
  voltar_contato: t('clients.status.followUp'),
});

export function useClientes(mostrarArquivados = false) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ['clientes', organizationId, { mostrarArquivados }],
    queryFn: () => listClientes(mostrarArquivados),
  });
}

export function useCliente(id: string | undefined) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ['cliente', organizationId, id],
    queryFn: () => (id ? getClienteById(id) : null),
    enabled: !!id,
  });
}

export function useAtualizarCliente() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: ClienteUpdate }) =>
      atualizarCliente(id, dados),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
    },
  });
}

export function useEditarCliente() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();

  return useMutation({
    mutationFn: ({
      id, dados, camposAlterados, profileId,
    }: {
      id: string;
      dados: ClienteUpdate;
      camposAlterados: string[];
      profileId?: string;
    }) => editarCliente({ id, dados, camposAlterados, profileId, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      toast({
        title: 'Dados atualizados',
        description: 'As informações do cliente foram salvas com sucesso.',
      });
    },
    onError: (error) => {
      captureError(error, { extra: { hook: 'useEditarCliente' } });
      toast({
        title: 'Erro ao atualizar',
        description: sanitizeSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
}

export function useAtualizarStatusCliente() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();
  const STATUS_LABELS = getStatusLabels(t);

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: StatusCliente }) =>
      atualizarStatusCliente({ id, status, orgId: requireOrgId(), statusLabel: STATUS_LABELS[status] }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      toast({
        title: 'Status alterado',
        description: `${data.nome} movido para "${STATUS_LABELS[data.status as StatusCliente]}"`,
      });
    },
    onError: (error) => {
      captureError(error, { extra: { hook: 'useAtualizarStatusCliente' } });
      toast({
        title: 'Erro ao alterar status',
        description: sanitizeSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
}

export function useCriarCliente() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { track, getUserProperty, setUserProperties } = useAnalytics();

  return useMutation({
    mutationFn: (dados: ClienteInsert) => criarCliente({ dados, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      if (!getUserProperty('has_created_first_client')) {
        track('activation_first_client_created');
        setUserProperties({ has_created_first_client: true });
      }
      toast({
        title: 'Cliente criado',
        description: 'Novo cliente potencial adicionado com sucesso.',
      });
    },
    onError: (error) => {
      captureError(error, { extra: { hook: 'useCriarCliente' } });
      toast({
        title: 'Erro ao criar cliente',
        description: sanitizeSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
}

export function useAssumirAtendimento() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      clienteId, profileId, profileNome,
    }: {
      clienteId: string;
      profileId: string;
      profileNome: string;
    }) => assumirAtendimento({ clienteId, profileId, profileNome, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      toast({
        title: t('panel.takenOverTitle'),
        description: t('panel.takenOverDesc'),
      });
    },
    onError: (error) => {
      captureError(error, { extra: { hook: 'useAssumirAtendimento' } });
      const mensagem = error instanceof ClienteJaAtribuidoError
        ? 'Este cliente já possui um responsável atribuído.'
        : sanitizeSupabaseError(error);
      toast({
        title: 'Erro ao assumir atendimento',
        description: mensagem,
        variant: 'destructive',
      });
    },
  });
}

export function useExcluirCliente() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { requireOrgId } = useOrganization();

  return useMutation({
    mutationFn: ({
      clienteId, clienteNome, profileId, profileNome,
    }: {
      clienteId: string;
      clienteNome?: string;
      profileId?: string;
      profileNome?: string;
    }) => excluirCliente({ clienteId, clienteNome, profileId, profileNome, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      navigate('/clientes');
      toast({
        title: 'Cliente excluído',
        description: 'O cliente foi removido do sistema.',
      });
    },
    onError: (error) => {
      captureError(error, { extra: { hook: 'useExcluirCliente' } });
      toast({
        title: 'Erro ao excluir cliente',
        description: sanitizeSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
}

export function useArquivarCliente() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();

  return useMutation({
    mutationFn: ({
      clienteId, clienteNome, profileId, profileNome,
    }: {
      clienteId: string;
      clienteNome?: string;
      profileId?: string;
      profileNome?: string;
    }) => arquivarCliente({ clienteId, clienteNome, profileId, profileNome, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      toast({
        title: 'Card arquivado',
        description: 'O card foi movido para arquivados. O cliente permanece ativo na lista.',
      });
    },
    onError: (error) => {
      captureError(error, { extra: { hook: 'useArquivarCliente' } });
      toast({
        title: 'Erro ao arquivar',
        description: sanitizeSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
}

export function useDesarquivarCliente() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();

  return useMutation({
    mutationFn: ({
      clienteId, clienteNome, novoStatus, profileId, profileNome,
    }: {
      clienteId: string;
      clienteNome?: string;
      novoStatus?: StatusCliente;
      profileId?: string;
      profileNome?: string;
    }) => desarquivarCliente({
      clienteId, clienteNome, novoStatus, profileId, profileNome, orgId: requireOrgId(),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      toast({
        title: 'Cliente reativado',
        description: 'O cliente voltou para a coluna "Voltar contato".',
      });
    },
    onError: (error) => {
      captureError(error, { extra: { hook: 'useDesarquivarCliente' } });
      toast({
        title: 'Erro ao reativar',
        description: sanitizeSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
}

export function useTransferirAtendimento() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();

  return useMutation({
    mutationFn: ({
      clienteId,
      novoResponsavelId,
      novoResponsavelNome,
      responsavelAnteriorId,
      responsavelAnteriorNome,
      profileId,
      profileNome,
    }: {
      clienteId: string;
      novoResponsavelId: string;
      novoResponsavelNome: string;
      responsavelAnteriorId?: string | null;
      responsavelAnteriorNome?: string | null;
      profileId: string;
      profileNome: string;
    }) => transferirAtendimento({
      clienteId,
      novoResponsavelId,
      novoResponsavelNome,
      responsavelAnteriorId,
      responsavelAnteriorNome,
      profileId,
      profileNome,
      orgId: requireOrgId(),
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      const responsavelNome = (data.responsavel as { nome?: string } | null)?.nome;
      toast({
        title: 'Atendimento transferido',
        description: `Cliente atribuído a ${responsavelNome || 'novo atendente'}.`,
      });
    },
    onError: (error) => {
      captureError(error, { extra: { hook: 'useTransferirAtendimento' } });
      toast({
        title: 'Erro ao transferir',
        description: sanitizeSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
}
